/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTitleLayout, ControlBtnGroup, SubTitleGroup } from 'components/layouts/ContentLayout';
import MapChart from 'components/mapchart/MapChart';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { useLoading } from 'components/process/Loading';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { useMessageBar } from 'hooks/useMessageBar';
import PropTypes from 'prop-types';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  findSetupMapChart,
  findSetupMapChartAuth,
  findSetupMapChartList,
  saveSetupMapChart,
  saveSetupMapChartImg,
} from 'apis/setup/SetupMapChart';
import { SetupMapChart, SetupMapChartCondition } from 'models/setup/SetupMapChart';
import { Code } from 'models/common/CommonCode';
import { SetupProject } from 'models/setup/SetupProject';
import { findProject } from 'apis/setup/SetupProject';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { BizName, FileTypeName } from 'models/common/Common';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { tabs } from 'components/layouts/Tab';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import CustomGrid from 'components/grids/CustomGrid';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { getExcelFileName } from 'utils/ExcelUtil';
import { NormalButton } from 'components/buttons/ButtonStyle';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from 'utils/SessionUtil';

const SetupProjectPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { elmPrjId } = useSessionStore();

  const defaultCondition = {
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjId: elmPrjId || '',
  };
  const ATCH_FILE_TP_CD = 'SETUP_MAP_CHART_IMG';

  const gridRef = useRef<any>();
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [setupMapChartCondition, setSetupMapChartCondition] =
    useState<SetupMapChartCondition>(defaultCondition);
  const [mapChartInfo, setMapChartInfo] = useState<SetupMapChart>({});
  const [errors, setErrors] = useState<any>({});
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [rowData, setRowData] = useState<SetupMapChart[]>([]);
  const [rmRowData, setRmRowData] = useState<SetupMapChart[]>([]);
  const [backupRowData, setBackupRowData] = useState<SetupMapChart[]>([]);
  const [isOpenFileUploadPopup, setIsOpenFileUploadPopup] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [isPrjAuth, setIsPrjAuth] = useState<boolean>(false);

  const tabMenus = useMemo(() => {
    return [
      { id: 'LIST', name: t('setup.label.설비 Mapping', '설비 Mapping') },
      { id: 'MAP', name: t('setup.label.Map Chart', 'Map Chart') },
    ];
  }, []);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (setupMapChartCondition?.bildPlntCds || []).length > 0
          ? setupMapChartCondition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, setupMapChartCondition?.bildPlntCds]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'eqpId',
        visible: true,
        header: String(t('setup.label.설비ID', '설비ID')),
        width: 240,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'eqpNm',
        visible: true,
        header: String(t('setup.label.설비명', '설비명')),
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'minXcrd',
        visible: true,
        header: String(t('setup.label.XMin', 'XMin')),
        align: 'center',
        format: 'n4',
      },
      {
        binding: 'maxXcrd',
        visible: true,
        header: String(t('setup.label.XMax', 'XMax')),
        align: 'center',
        format: 'n4',
      },
      {
        binding: 'minYcrd',
        visible: true,
        header: String(t('setup.label.YMin', 'YMin')),
        align: 'center',
        format: 'n4',
      },
      {
        binding: 'maxYcrd',
        visible: true,
        header: String(t('setup.label.YMax', 'YMax')),
        align: 'center',
        format: 'n4',
      },
      {
        binding: 'ctrXcrd',
        visible: true,
        header: String(t('setup.label.XCenter', 'XCenter')),
        align: 'center',
        format: 'n4',
      },
      {
        binding: 'ctrYcrd',
        visible: true,
        header: String(t('setup.label.YCenter', 'YCenter')),
        align: 'center',
        format: 'n4',
      },
    ];
  }, [rowData]);

  const drwImageSize = useMemo(() => {
    if (mapChartInfo?.imgWthLen && mapChartInfo?.imgVtcLen) {
      return `${mapChartInfo.imgWthLen} * ${mapChartInfo.imgVtcLen}`;
    } else {
      return ``;
    }
  }, [mapChartInfo]);

  const downloadExcelTemplate = async (templateType) => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    openLoading(true);
    downloadExcelTemplatesPost(templateType, mapChartInfo?.elmPrjId || '', []).finally(() =>
      openLoading(false)
    );
  };

  const callbackExcelUpload = async (file: FileList | null) => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      FileTypeName.TPL_SETUP_MAP_CHART,
      'NORMAL',
      'TB_EELMB_PRJ_TGT_EQP_M',
      setupMapChartCondition?.elmPrjId
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          btnSearch(setupMapChartCondition);
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const btnSearch = (condition: SetupMapChartCondition) => {
    // validation check
    if (!validate()) return;

    openLoading(true);
    Promise.all([
      findSetupMapChart(condition?.elmPrjId || ''),
      findSetupMapChartList(condition),
      findSetupMapChartAuth(condition?.elmPrjId || ''), // 프로젝트 담당자(본사, 법인) 권한 체크
    ])
      .then(([res1, res2, auth]) => {
        if (res1 !== null) {
          setMapChartInfo({ ...res1 });
        }

        if (res2 !== null) {
          setRowData(res2.map((o) => ({ ...o, selected: true })) || []);
          setBackupRowData(res2.map((o) => ({ ...o })) || []);
        }
        setIsPrjAuth(!!auth);
      })
      .catch(() => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const handleOnChange = (name: string, value: any) => {
    setSetupMapChartCondition((prev) => ({ ...prev, [name]: value }));
    if (name !== 'bildPlntCds') setErrors((prev: any) => ({ ...prev, [name]: !value }));
  };

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);

    findProject({ searchVisibleSaveYn: 'N' }).then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const validate = () => {
    const err = {};

    // 프로젝트
    if (!setupMapChartCondition?.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }

    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  const openImgFileUpload = () => {
    // validation check
    if (!validate()) return;

    setIsOpenFileUploadPopup(true);
  };

  /**
   * 도면 이미지 파일 삭제
   */
  const onDeleteImg = () => {
    callbackFileUpload('');
  };

  /**
   * 도면 이미지 파일 저장
   */
  const callbackFileUpload = async (atchFileGrId: string) => {
    openLoading(true);
    const res = await saveSetupMapChartImg({
      ...setupMapChartCondition,
      drwAtchFileId: atchFileGrId,
    });
    if (res.data) {
      openMessageBar({
        type: 'confirm',
        content: t('com.msg.저장되었습니다.', '저장되었습니다.'),
      });
      setIsOpenFileUploadPopup(false);
      btnSearch(setupMapChartCondition);
    }
    openLoading(false);
  };

  /***** Grid 관련 함수 start *****/
  // Grid 새로고침
  const onResetGrid = () => {
    setRowData(backupRowData.map((o) => ({ ...o })));
  };
  // Grid 초기세팅
  const onInitialized = (grid) => {
    gridRef.current = grid;
  };
  // Grid Edit Event
  const onBeginningEdit = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    if (['eqpId', 'eqpNm'].includes(binding)) {
      e.cancel = true;
    }
  };
  // Grid Cell Edit 완료시 Event
  const onCellEditEnded = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    const rowIndex = e.row;
    const colIndex = e.col;
    const newValue = grid.getCellData(rowIndex, colIndex, false);
    setRowData((prev) =>
      prev.map((o, i) => {
        if (i === rowIndex) o[binding] = parseFloat(newValue);
        return o;
      })
    );
  };
  /***** Grid 관련 함수 end ******/

  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('setup.label.MapChart', 'MapChart');
    book.saveAsync(getExcelFileName(t('setup.label.MapChart', 'MapChart')));
  };

  const saveRowData = () => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }

    openLoading(true);
    saveSetupMapChart(rowData.filter((o) => o.crudKey === 'U'))
      .then((res) => {
        if (res.data) {
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          btnSearch({ elmPrjId: mapChartInfo.elmPrjId || '' });
        }
      })
      .finally(() => openLoading(false));
  };

  const removeRowData = () => {
    // validation check
    if (!mapChartInfo?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 조회해주세요.', '프로젝트를 조회해주세요.'),
      });
      return;
    }
    if (rmRowData.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.삭제할 데이터를 선택해주세요.', '삭제할 데이터를 선택해주세요.'),
      });
      return;
    }

    openLoading(true);
    saveSetupMapChart(rmRowData)
      .then((res) => {
        if (res.data) {
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          btnSearch({ elmPrjId: mapChartInfo.elmPrjId || '' });
        }
      })
      .finally(() => openLoading(false));
  };

  useEffect(() => {
    const init = async () => {
      await initCodes();
    };
    init();
  }, []);

  useEffect(() => {
    console.log(rmRowData);
  }, [rmRowData]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupMapChartCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupMapChartCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={setupMapChartCondition?.elmPrjId}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.msg.프로젝트를 선택해 주세요.', '프로젝트를 선택해 주세요.')
                  )}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setSetupMapChartCondition}
              onSearch={() => btnSearch(setupMapChartCondition)}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.맵차트 등록', '맵차트 등록')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('setup.label.공장 Layout', '공장 Layout')}</TableCell>
              <TableCell>
                <span className="ellipsis">
                  <NormalButton
                    css={IconButton.button}
                    size="sm"
                    className="icoNone mr"
                    onClick={openImgFileUpload}
                  >
                    {t('com.button.이미지 등록', '이미지 등록')}
                  </NormalButton>
                  {mapChartInfo.drwAtchFileNm}
                  {mapChartInfo.drwAtchFileId && (
                    <NormalButton
                      css={IconButton.button}
                      size="sm"
                      className="icoNone mr"
                      onClick={onDeleteImg}
                    >
                      {t('com.button.삭제', '삭제')}
                    </NormalButton>
                  )}
                </span>
              </TableCell>
              <TableCell>{t('setup.label.Size', 'Size')}</TableCell>
              <TableCell>{drwImageSize}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <div css={tabs.wrap} className="mt-6">
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-schedule-tab-${idx}`}
              aria-controls={`setup-schedule-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
        {currentTabIndex === 0 && (
          <div css={tabs.btn}>
            {(isPrjAuth || hasRole('FA_MANAGER')) && (
              <>
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={() => downloadExcelTemplate(FileTypeName.TPL_SETUP_MAP_CHART)}
                  disableRipple
                >
                  {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
                </Button>
                <ExcelUploadButton onCallback={(file) => callbackExcelUpload(file)} />
                <Button css={IconButton.button} className="save" onClick={saveRowData}>
                  {t('com.button.저장', '저장')}
                </Button>
                <Button css={IconButton.button} className="delete" onClick={removeRowData}>
                  {t('com.button.삭제', '삭제')}
                </Button>
              </>
            )}
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={() => handleDownloadExcel()}
              disableRipple
            >
              {t('com.button.다운로드', '다운로드')}
            </Button>
            <Button css={IconButton.button} className="refresh" onClick={onResetGrid}>
              {t('com.button.새로고침', '새로고침')}
            </Button>
          </div>
        )}
      </div>
      <TabPanel value={currentTabIndex} index={0}>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          align={'center'}
          isSelector={true}
          initialized={onInitialized}
          beginningEdit={onBeginningEdit}
          cellEditEnded={onCellEditEnded}
          onChangeCheckedItem={(checkedItems) => {
            setRmRowData(
              checkedItems.map((o) => ({
                ...o,
                ctrXcrd: 0,
                minXcrd: 0,
                maxXcrd: 0,
                ctrYcrd: 0,
                minYcrd: 0,
                maxYcrd: 0,
              }))
            );
          }}
        />
      </TabPanel>
      <TabPanel value={currentTabIndex} index={1}>
        {rowData.length > 0 && mapChartInfo.drwAtchFileId && (
          <MapChart
            className="border height_1"
            polygonData={rowData}
            setPolygonData={setRowData}
            imageId={mapChartInfo.drwAtchFileId || ''}
            imageWidth={parseInt(mapChartInfo.imgWthLen || '0')}
            imageHeight={parseInt(mapChartInfo.imgVtcLen || '0')}
            showLegend={false}
            showTooltip={true}
            badgeKey="no-key"
          />
        )}
      </TabPanel>

      {isOpenFileUploadPopup && (
        // 도면 이미지 업로드 Modal
        <FileUploadPopUp
          open={isOpenFileUploadPopup}
          close={() => {
            setIsOpenFileUploadPopup(false);
          }}
          singleSelect={true}
          showAllDownload={false}
          initParam={{
            atchFileTpCd: ATCH_FILE_TP_CD,
            optValCtn1: 'tb_eelmb_prj_m',
            bizName: BizName.SETUP,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            if (atchFileGrId && fileCount > 0) {
              callbackFileUpload(atchFileGrId);
            }
          }}
        />
      )}
      {isExcelValidModalOpen && (
        // 엑셀 템플릿 업로드 검증 Modal
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default SetupProjectPage;
